import { motion } from 'framer-motion';
import { useState } from 'react';
import Navigation from '../home/components/Navigation';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    service: '',
    budget: '',
    message: '',
    timeline: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const formDataToSend = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formDataToSend.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d4t8gra2t66nrlpqepb0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formDataToSend
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          company: '',
          service: '',
          budget: '',
          message: '',
          timeline: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: 'ri-mail-line',
      title: 'Email Us',
      value: 'hello@blackvelvetstudio.com',
      description: 'Send us an email anytime'
    },
    {
      icon: 'ri-phone-line',
      title: 'Call Us',
      value: '+1 (555) 123-4567',
      description: 'Mon-Fri 9AM-6PM EST'
    },
    {
      icon: 'ri-map-pin-line',
      title: 'Visit Us',
      value: 'New York, NY',
      description: 'Creative Studio Location'
    }
  ];

  const services = [
    'AI Image Generation',
    'Cinematic Videos', 
    'Hybrid Content',
    'UGC Content',
    'Custom Project',
    'Consultation'
  ];

  const budgetRanges = [
    'Under $5,000',
    '$5,000 - $15,000',
    '$15,000 - $50,000',
    '$50,000 - $100,000',
    '$100,000+',
    'Let\'s Discuss'
  ];

  const timelines = [
    'ASAP',
    '1-2 weeks',
    '1 month',
    '2-3 months',
    '3+ months',
    'Flexible'
  ];

  return (
    <div className="min-h-screen bg-black text-white">
      <Navigation />
      
      {/* Hero Section */}
      <section className="relative pt-40 pb-20 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/b731edccc4fd815aee48dbe42814b134.jpg"
            alt="Contact Black Velvet Studio"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-black/90"></div>
        </div>
        
        <div className="relative max-w-[1400px] mx-auto px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="inline-flex items-center gap-3 px-6 py-3 rounded-full bg-gradient-to-r from-brand-pink/20 to-brand-aqua/20 backdrop-blur-xl border border-brand-pink/30 mb-8">
              <i className="ri-message-3-line text-brand-pink text-xl"></i>
              <span className="text-sm font-semibold text-white">Get In Touch</span>
            </div>
            
            <h1 className="text-7xl font-light mb-8">
              <span className="text-white">Let's Create </span>
              <span className="bg-gradient-to-r from-brand-pink to-brand-aqua bg-clip-text text-transparent">Together</span>
            </h1>
            
            <p className="text-2xl text-white/80 max-w-4xl mx-auto leading-relaxed">
              Ready to bring your vision to life? We'd love to discuss your project 
              and explore how AI-powered creativity can elevate your brand.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Info */}
      <section className="py-20">
        <div className="max-w-[1400px] mx-auto px-12">
          <div className="grid grid-cols-3 gap-8 mb-20">
            {contactInfo.map((info, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                whileHover={{ y: -5 }}
                className="text-center p-8 rounded-2xl bg-gradient-to-b from-white/5 to-white/2 border border-white/10 backdrop-blur-xl"
              >
                <div className="w-16 h-16 flex items-center justify-center rounded-full bg-gradient-to-r from-brand-pink/20 to-brand-aqua/20 mx-auto mb-6">
                  <i className={`${info.icon} text-2xl bg-gradient-to-r from-brand-pink to-brand-aqua bg-clip-text text-transparent`}></i>
                </div>
                <h3 className="text-xl font-semibold text-white mb-2">{info.title}</h3>
                <p className="text-lg text-brand-pink mb-2">{info.value}</p>
                <p className="text-white/60">{info.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section className="pb-32">
        <div className="max-w-[1000px] mx-auto px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-5xl font-light mb-6">
              <span className="text-white">Start Your </span>
              <span className="bg-gradient-to-r from-brand-pink to-brand-aqua bg-clip-text text-transparent">Project</span>
            </h2>
            <p className="text-xl text-white/60">
              Tell us about your vision and we'll get back to you within 24 hours.
            </p>
          </motion.div>

          <motion.form
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            onSubmit={handleSubmit}
            data-readdy-form
            id="contact-form-submission"
            className="space-y-8 p-12 rounded-3xl bg-gradient-to-b from-white/5 to-white/2 border border-white/10 backdrop-blur-xl"
          >
            {/* Name & Email Row */}
            <div className="grid grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-semibold text-white/80 mb-3">
                  Full Name *
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  className="w-full px-6 py-4 bg-black/40 border border-white/20 rounded-xl text-white placeholder-white/40 focus:border-brand-pink focus:outline-none transition-colors text-sm"
                  placeholder="Your full name"
                />
              </div>
              
              <div>
                <label className="block text-sm font-semibold text-white/80 mb-3">
                  Email Address *
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  className="w-full px-6 py-4 bg-black/40 border border-white/20 rounded-xl text-white placeholder-white/40 focus:border-brand-pink focus:outline-none transition-colors text-sm"
                  placeholder="your@email.com"
                />
              </div>
            </div>

            {/* Company */}
            <div>
              <label className="block text-sm font-semibold text-white/80 mb-3">
                Company/Organization
              </label>
              <input
                type="text"
                name="company"
                value={formData.company}
                onChange={handleInputChange}
                className="w-full px-6 py-4 bg-black/40 border border-white/20 rounded-xl text-white placeholder-white/40 focus:border-brand-pink focus:outline-none transition-colors text-sm"
                placeholder="Your company name"
              />
            </div>

            {/* Service & Budget Row */}
            <div className="grid grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-semibold text-white/80 mb-3">
                  Service Needed *
                </label>
                <select
                  name="service"
                  value={formData.service}
                  onChange={handleInputChange}
                  required
                  className="w-full px-6 py-4 bg-black/40 border border-white/20 rounded-xl text-white focus:border-brand-pink focus:outline-none transition-colors text-sm"
                >
                  <option value="">Select a service</option>
                  {services.map((service, index) => (
                    <option key={index} value={service}>{service}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-semibold text-white/80 mb-3">
                  Budget Range
                </label>
                <select
                  name="budget"
                  value={formData.budget}
                  onChange={handleInputChange}
                  className="w-full px-6 py-4 bg-black/40 border border-white/20 rounded-xl text-white focus:border-brand-pink focus:outline-none transition-colors text-sm"
                >
                  <option value="">Select budget range</option>
                  {budgetRanges.map((budget, index) => (
                    <option key={index} value={budget}>{budget}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Timeline */}
            <div>
              <label className="block text-sm font-semibold text-white/80 mb-3">
                Project Timeline
              </label>
              <select
                name="timeline"
                value={formData.timeline}
                onChange={handleInputChange}
                className="w-full px-6 py-4 bg-black/40 border border-white/20 rounded-xl text-white focus:border-brand-pink focus:outline-none transition-colors text-sm"
              >
                <option value="">Select timeline</option>
                {timelines.map((timeline, index) => (
                  <option key={index} value={timeline}>{timeline}</option>
                ))}
              </select>
            </div>

            {/* Message */}
            <div>
              <label className="block text-sm font-semibold text-white/80 mb-3">
                Project Details *
              </label>
              <textarea
                name="message"
                value={formData.message}
                onChange={handleInputChange}
                required
                maxLength={500}
                rows={6}
                className="w-full px-6 py-4 bg-black/40 border border-white/20 rounded-xl text-white placeholder-white/40 focus:border-brand-pink focus:outline-none transition-colors text-sm resize-none"
                placeholder="Tell us about your project, goals, and any specific requirements..."
              />
              <p className="text-xs text-white/40 mt-2">{formData.message.length}/500 characters</p>
            </div>

            {/* Submit Status */}
            {submitStatus === 'success' && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="p-4 rounded-xl bg-green-500/20 border border-green-500/30 text-green-400 text-center"
              >
                <i className="ri-check-line text-xl mr-2"></i>
                Thank you! We'll get back to you within 24 hours.
              </motion.div>
            )}

            {submitStatus === 'error' && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="p-4 rounded-xl bg-red-500/20 border border-red-500/30 text-red-400 text-center"
              >
                <i className="ri-error-warning-line text-xl mr-2"></i>
                Something went wrong. Please try again or email us directly.
              </motion.div>
            )}

            {/* Submit Button */}
            <motion.button
              type="submit"
              disabled={isSubmitting}
              whileHover={{ scale: isSubmitting ? 1 : 1.02 }}
              whileTap={{ scale: isSubmitting ? 1 : 0.98 }}
              className={`w-full py-5 rounded-xl font-bold text-lg transition-all whitespace-nowrap ${
                isSubmitting
                  ? 'bg-white/20 text-white/60 cursor-not-allowed'
                  : 'bg-gradient-to-r from-brand-pink to-brand-aqua text-white cursor-pointer hover:shadow-2xl'
              }`}
            >
              {isSubmitting ? (
                <span className="flex items-center justify-center gap-3">
                  <i className="ri-loader-4-line animate-spin"></i>
                  Sending Message...
                </span>
              ) : (
                <span className="flex items-center justify-center gap-3">
                  Send Message
                  <i className="ri-send-plane-line"></i>
                </span>
              )}
            </motion.button>
          </motion.form>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;